import type { ReactNode } from "react";
import { CloseLinearIcon } from "../../icons";
import { Button } from "../Button";

export interface CalendarPanelProps {
  /** e.g. "Pick a day:" -- the design's header. */
  readonly title: string;
  readonly closeLabel: string;
  readonly saveLabel: string;
  readonly cancelLabel: string;
  /** Applies the picked date(s) and closes. */
  readonly onSave: () => void;
  /** Closes without applying -- the close button and Cancel both. */
  readonly onCancel: () => void;
  /** Nothing complete to apply yet (e.g. a range with only its start). */
  readonly saveDisabled?: boolean;
  readonly children: ReactNode;
}

/**
 * The picker popup's chrome (Figma Date & Time pickers 2818:116890): a grey
 * panel with a close button and title over the white calendar card, and
 * Save / Cancel under it. Picks are a draft until Save. Shared by DatePicker
 * and DateRangePicker so both read and behave the same.
 */
export function CalendarPanel({
  title,
  closeLabel,
  saveLabel,
  cancelLabel,
  onSave,
  onCancel,
  saveDisabled = false,
  children,
}: CalendarPanelProps) {
  return (
    <div className="flex flex-col gap-3 px-2 py-4">
      <div className="flex items-center gap-2 p-2">
        <button
          type="button"
          onClick={onCancel}
          aria-label={closeLabel}
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-text-placeholder hover:text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        >
          <CloseLinearIcon className="size-4" aria-hidden="true" />
        </button>
        <p className="flex-1 text-start text-body-m-r text-text-placeholder">
          {title}
        </p>
      </div>
      {children}
      {/* Figma: Save fills the row on the start side, Cancel stays its own
          width at the end. */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          className="h-11 flex-1"
          disabled={saveDisabled}
          onClick={onSave}
        >
          {saveLabel}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="h-11"
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
}

/** The popup's own surface: grey, bordered, small shadow (applied to
 *  PopoverContent). */
export const CALENDAR_POPOVER_CLASS =
  "w-auto min-w-78 rounded-lg border border-container-default-border bg-neutral-50 p-0 shadow-sm";
